import { ChangeEvent, FormEvent, useState } from "react"
import H3 from "../../../atoms/typography/H3"
import Body from "../../../atoms/typography/Body"
import { HorizontalSpacer, Spacer } from "../../../atoms/spacers"
import TextInput from "../../TextInput"
import PrimaryButton from "../../../atoms/buttons/PrimaryButton"
import { checkEmailValid } from "../../../../utils/helpers"
import CustomCheckbox from "../../../atoms/inputs/CustomCheckbox"
import { theme } from "../../../theme"
import { Link } from "react-router-dom"
import { SendProductSheetEmailInput } from "../../../../graphql/mutations/sendProductSheet"
import TextButton from "../../../atoms/buttons/TextButton"
import styled from "styled-components"
import { MediaQuery } from "../../../../utils/design-helpers"
import { ShareIcon } from "../../../atoms/icons/ShareIcon"
import { Hidden, Visible } from "react-grid-system"
import { captureExceptionWithContext } from "../../../../utils/sentry"

type Props = {
  handleSubmit: (input: SendProductSheetEmailInput) => Promise<void>
  productSheetType: string | undefined
  isSent: boolean
  setIsSent: (isSent: boolean) => void
  errors: productSheetErrors[]
  link?: string
}

interface productSheetErrors {
  field: string
  messages: string
}

const FlexDiv = styled.div`
  display: flex;
  align-items: center;

  ${MediaQuery.XS} {
    flex-direction: column;
  }
`

export const SendProductSheetForm = ({
  handleSubmit,
  productSheetType = "satcube_ku",
  isSent,
  setIsSent,
  errors,
  link,
}: Props) => {
  const [email, setEmail] = useState("")
  const [company, setCompany] = useState("")
  const [jobTitle, setJobTitle] = useState("")
  const [agreedTerms, setAgreedTerms] = useState(false)

  const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    setIsSent(false)
  }

  const onsubmit = async (e: FormEvent) => {
    e.preventDefault()
    let input: SendProductSheetEmailInput = {
      acceptTc: agreedTerms,
      companyName: company,
      jobTitle: jobTitle,
      email: email,
      typeOfSheet: productSheetType,
    }
    try {
      await handleSubmit(input)
    } catch (error) {
      captureExceptionWithContext(error, "error submitting product sheet form")
    }
  }
  return (
    <form onSubmit={onsubmit}>
      <div>
        <Body>Company:</Body>
        <HorizontalSpacer spacing={0.5} />
        <TextInput
          type="text"
          name="company"
          placeholder="Company name *"
          onChange={(e) => setCompany(e.target.value)}
          value={company}
          tabIndex={1}
          required
        />
        {errors.some((e) => e.field === "companyName") && (
          <div>
            <HorizontalSpacer spacing={0.5} />
            <H3 color={theme.colors.red}>{errors.find((e) => e.field === "companyName")?.messages}</H3>
          </div>
        )}
        <HorizontalSpacer spacing={2} />
        <Body>Job Title:</Body>
        <HorizontalSpacer spacing={0.5} />
        <TextInput
          type="text"
          name="jobTitle"
          placeholder="Job title"
          onChange={(e) => setJobTitle(e.target.value)}
          value={jobTitle}
          tabIndex={2}
        />
        {errors.some((e) => e.field === "jobTitle") && (
          <div>
            <HorizontalSpacer spacing={0.5} />
            <H3 color={theme.colors.red}>{errors.find((e) => e.field === "jobTitle")?.messages}</H3>
          </div>
        )}
        <HorizontalSpacer spacing={2} />
        <Body>Email:</Body>
        <HorizontalSpacer spacing={0.5} />
        <TextInput
          type="email"
          name="email"
          placeholder="Email address *"
          onChange={onChangeEmail}
          value={email}
          tabIndex={3}
          required
        />
        {errors.some((e) => e.field === "email") && (
          <div>
            <HorizontalSpacer spacing={0.5} />
            <H3 color={theme.colors.red}>{errors.find((e) => e.field === "email")?.messages}</H3>
          </div>
        )}
        <HorizontalSpacer spacing={2} />
        <Hidden xs>
          <FlexDiv>
            <CustomCheckbox
              type="checkbox"
              onChange={() => setAgreedTerms(!agreedTerms)}
              checked={agreedTerms}
              tabIndex={4}
              required
            />
            <Spacer spacing={1} />
            <H3>
              <Link to="/terms-and-conditions" target="_blank">
                <TextButton title="I accept the Terms and Conditions" icon={<ShareIcon />} />
              </Link>
            </H3>
          </FlexDiv>
        </Hidden>
        <Visible xs>
          <CustomCheckbox
            type="checkbox"
            onChange={() => setAgreedTerms(!agreedTerms)}
            checked={agreedTerms}
            tabIndex={4}
            required
          />
          <Link to="/terms-and-conditions" target="_blank">
            <TextButton title="I accept the Terms and Conditions" />
          </Link>
        </Visible>
        {errors.some((e) => e.field === "acceptTc") && (
          <div>
            <HorizontalSpacer spacing={0.5} />
            <H3 color={theme.colors.red}>{errors.find((e) => e.field === "acceptTc")?.messages}</H3>
          </div>
        )}
        <HorizontalSpacer spacing={2} />
      </div>
      <HorizontalSpacer spacing={1.5} />
      {isSent && link && (
        <div>
          <a href={link} target="__blank">
            Download your product sheet
          </a>
          <HorizontalSpacer spacing={1.5} />
        </div>
      )}
      <PrimaryButton
        type="submit"
        title={isSent ? "Received!" : "Receive product sheet"}
        disabled={!checkEmailValid(email) || isSent || !(email && company && agreedTerms)}
      />
    </form>
  )
}
